import * as d3 from 'd3'
import { isMobile } from 'react-device-detect'
import { countries } from 'country-data'
import Chart from '../../ultils/Chart'
import d3tip from '../../ultils/d3-tip'
import './styles.css'
import cursorSvg from '../../assets/images/Vector.svg'
import {
  RISK_STATUS_COLOR_SCALE,
  RISK_STATUS_CONST,
  RISK_TYPE,
} from '../../ultils/chart-d3-utils'

class MarblesChart extends Chart {
  init({ data, redirect, group, isEmptyData, riskType }) {
    let vis = this
    vis.data = data
    vis.riskType = riskType
    vis.originalLength = data.length
    vis.redirect = redirect
    vis.group = group
    vis.isEmptyData = isEmptyData

    vis.tip = d3tip()
      .attr('class', 'd3-tip-marble-chart')
      .html((e, d) => vis.generateTooltipHtml(d))
      .positionBelow(10)

    vis.openTicker = null
    vis.hoveredTicker = null

    vis.t = () => d3.transition().duration(500)

    vis.x = d3.scaleBand().domain(RISK_STATUS_CONST).padding(0.3)

    vis.colorScale = RISK_STATUS_COLOR_SCALE

    vis.simulation = d3
      .forceSimulation()
      .force(
        'y',
        d3
          .forceY()
          .strength(0.1)
          .y(vis.HEIGHT / 2 + 16),
      )
      .force('charge', d3.forceManyBody().strength(1)) // Nodes are attracted one each other of value is > 0
      .alphaTarget(0.03)

    vis.dragstarted = function (event, d) {
      if (!event.active) vis.simulation.alphaTarget(0.03).restart()
      d.fx = event.x
      d.fy = event.y
    }
    vis.dragged = function (event, d) {
      d.fx = event.x
      d.fy = event.y
    }
    vis.dragended = function (event, d) {
      if (!event.active) vis.simulation.alphaTarget(0.03)
      d.fx = null
      d.fy = null
    }

    // first time, run by risk for sorting
    vis.update({ group: 'risk' })

    vis.g.call(vis.tip)
  }

  generateTooltipHtml(d) {
    let returnDiv = `<div class="tip-content">
            <div class="tip-header">
              <div class="tip-title">
                  ${d.proper_name}
              </div>
              <div class="tip-content">
                <div class="row">
                  <div class="row-title">Ticker</div>
                  <div class="row-content" id="ticker-id">${d.ticker}</div>
                </div>
                <div class="row">
                  <div class="row-title">Country</div>
                  <div class="row-content">${
                    countries[d.iso_country].name
                  }</div>
                </div>
                <div class="row">
                  <div class="row-title">Sector</div>
                  <div class="row-content">${d.factset_sector_desc}</div>
                </div>
                <div class="row">
                  <div class="row-title">Status Date</div>
                  <div class="row-content">${d3.timeFormat('%d %B %Y')(
                    new Date(d.date),
                  )}</div>
                </div>
        `
    returnDiv += `
              </div>
            </div>
          </div>`
    return returnDiv
  }

  update({ group, data, riskType }) {
    let vis = this
    vis.group = group || vis.group
    vis.data = data || vis.data
    vis.riskType = riskType || vis.riskType

    const dotStrokeWidth = 1.6
    // const dotRadius =
    //   vis.data.length > 300 ? 5 : vis.data.length < 5 ? 20 : isMobile ? 8 : 10
    const dotRadius = 16 / 2

    const hoverRingRadius = Math.max(10, dotRadius + 2)
    const baseFillOpacity = 0.55
    const baseStrokeOpacity = 0.85
    const centerX = vis.WIDTH / 2
    const centerY = vis.HEIGHT / 2
    const maxRadius = Math.max(
      1,
      Math.min(vis.WIDTH, vis.HEIGHT) / 2 - dotRadius - 6,
    )
    const fadeStart = maxRadius * 0.65
    const fadeRange = Math.max(1, maxRadius - fadeStart)
    const getOuterFade = d => {
      const x = Number.isFinite(d.x) ? d.x : centerX
      const y = Number.isFinite(d.y) ? d.y : centerY
      const dist = Math.hypot(x - centerX, y - centerY)
      const t = (dist - fadeStart) / fadeRange
      return Math.max(0, Math.min(1, t))
    }
    const getFillOpacity = d => {
      const fade = getOuterFade(d)
      return baseFillOpacity * (1 - 0.18 * fade)
    }
    const getStrokeOpacity = d => {
      const fade = getOuterFade(d)
      return baseStrokeOpacity * (1 - 0.55 * fade)
    }
    const getStrokeWidth = d => {
      const fade = getOuterFade(d)
      return dotStrokeWidth * (1 - 0.35 * fade)
    }
    const cursorStyle = `url(${cursorSvg}) 10 12, pointer`
    const clampX = x => {
      const value = Number.isFinite(x) ? x : vis.WIDTH / 2
      return Math.max(20, Math.min(vis.WIDTH - 20, value))
    }
    const clampY = y => {
      const value = Number.isFinite(y) ? y : vis.HEIGHT / 2
      return Math.max(20, Math.min(vis.HEIGHT - 20, value))
    }
    const updateHoverRingPosition = d => {
      if (!d) {
        vis.hoveredTicker = null
        vis.hoverRing.style('opacity', 0)
        return
      }
      vis.hoveredTicker = d.ticker
      vis.hoverRing
        .attr('cx', clampX(d.x ?? vis.WIDTH / 2))
        .attr('cy', clampY(d.y ?? vis.HEIGHT / 2))
        .style('opacity', 1)
    }

    vis.x.range([0, vis.WIDTH])

    vis.simulation.force(
      'collide',
      d3
        .forceCollide()
        .strength(0.05)
        .radius(vis.data.length > 300 ? 20 : vis.data.length < 5 ? 40 : 27)
        .iterations(1),
    ) // Force that avoids circle overlapping

    // sort bubbles
    if (vis.data.length === vis.originalLength) {
      vis.simulation
        .force(
          'x',
          d3
            .forceX()
            .strength(1)
            .x(
              d =>
                vis.x(
                  vis.riskType === RISK_TYPE.CREDIT ? d.credit_status : ((!d.risk_outlook || d.risk_outlook != "null") ? d.risk_outlook : 'Neutral'),
                ) + vis.x.bandwidth(),
            ),
        )
        .alpha(0.5)
    }

    vis.svg.selectAll('image').remove()

    d3.selection.prototype.dblTap = function (callback) {
      let last = 0
      return this.each(function () {
        d3.select(this).on('touchstart', function (e, d) {
          if (e.timeStamp - last < 500) {
            return callback(e, d)
          }
          last = e.timeStamp
        })
      })
    }

    vis.g.selectAll('circle').remove()
    const node = vis.g
      .selectAll('circle')
      .data(vis.data, d => d.ticker)
      .join(
        enter =>
          enter
            .append('circle')
            .on('mouseout', () => {
              vis.tip.hide()
              updateHoverRingPosition(null)
            })
            .on('mouseover', (e, d) => {
              vis.openTicker = d.ticker
              updateHoverRingPosition(d)
              vis.tip.show(e, d, 6, 6)
            })
            .attr('cx', vis.WIDTH / 2)
            .attr('cy', vis.HEIGHT / 2)
            .attr(
              'fill',
              d =>
                vis.colorScale[
                  vis.riskType === RISK_TYPE.CREDIT ? d.credit_status : ((!d.risk_outlook || d.risk_outlook != "null") ? d.risk_outlook : 'Neutral')
                ] || 'black',
            )
            .attr(
              'stroke',
              d =>
                vis.colorScale[
                  vis.riskType === RISK_TYPE.CREDIT ? d.credit_status : ((!d.risk_outlook || d.risk_outlook != "null") ? d.risk_outlook : 'Neutral')
                ] || 'black',
            )
            .attr('fill-opacity', d => getFillOpacity(d))
            .attr('stroke-opacity', d => getStrokeOpacity(d))
            .attr('stroke-width', d => getStrokeWidth(d))
            .style('cursor', cursorStyle)
            .on('click', (e, d) => {
              if (!isMobile) {
                vis.tip.hide()
                vis.redirect(`/company/${d.ticker}`, { fromChart: true })
              }
            })
            .dblTap((e, d) => {
              if (isMobile) {
                vis.tip.hide()
                vis.redirect(`/company/${d.ticker}`, { fromChart: true })
              }
            })
            .call(
              d3
                .drag()
                .on('start', vis.dragstarted)
                .on('drag', vis.dragged)
                .on('end', vis.dragended),
            )
            .call(enter =>
              enter
                .attr('fill-opacity', 0)
                .attr('stroke-opacity', 0)
                .attr('stroke-width', 0)
                .attr('r', 0)
                .transition(vis.t())
                .attr('fill-opacity', d => getFillOpacity(d))
                .attr('stroke-opacity', d => getStrokeOpacity(d))
                .attr('stroke-width', d => getStrokeWidth(d))
                .attr('r', dotRadius),
            ),
        update =>
          update.call(update =>
            update
              .transition(vis.t())
              .attr('fill-opacity', d => getFillOpacity(d))
              .attr('stroke-opacity', d => getStrokeOpacity(d))
              .attr('stroke-width', d => getStrokeWidth(d))
              .attr('r', dotRadius),
          ),
        exit =>
          exit.call(exit =>
            exit
              .transition(vis.t())
              .attr('fill-opacity', 0)
              .attr('stroke-opacity', 0)
              .attr('r', 0)
              .attr('stroke-width', 0)
              .remove(),
          ),
      )

    vis.hoverRing = vis.g
      .selectAll('.marble-hover-ring')
      .data([null])
      .join('circle')
      .attr('class', 'marble-hover-ring')
      .attr('r', hoverRingRadius)
      .attr('fill', 'none')
      .attr('stroke', '#1D82F9')
      .attr('stroke-width', 3)
      .style('pointer-events', 'none')
      .style('opacity', 0)

    vis.simulation
      .nodes(vis.data, d => d.ticker)
      .on('tick', () => {
        node
          .attr('cx', d => (d.x = clampX(d.x)))
          .attr('cy', d => (d.y = clampY(d.y)))
          .attr('fill-opacity', d => getFillOpacity(d))
          .attr('stroke-opacity', d => getStrokeOpacity(d))
          .attr('stroke-width', d => getStrokeWidth(d))

        if (vis.hoveredTicker) {
          const hovered = vis.data.find(d => d.ticker === vis.hoveredTicker)
          if (hovered) updateHoverRingPosition(hovered)
        }
      })

    // wait for circles to sort => then set actual values
    setTimeout(() => {
      vis.simulation
        .force(
          'x',
          d3
            .forceX()
            .strength(vis.group === 'none' ? 0.1 : 0.5)
            .x(
              vis.group === 'none'
                ? vis.WIDTH / 2
                : d =>
                    vis.x(
                      vis.riskType === RISK_TYPE.CREDIT
                        ? d.credit_status
                        : ((!d.risk_outlook || d.risk_outlook != "null") ? d.risk_outlook : 'Neutral'),
                    ) + vis.x.bandwidth(),
            ),
        )
        .force(
          'center',
          vis.group === 'none'
            ? d3.forceCenter(vis.WIDTH / 2, vis.HEIGHT / 2)
            : null,
        )
        .alphaTarget(0.05)
    }, 300)
  }
}

export default MarblesChart
